// WhatsApp Service for handling auto-replies and message management

export interface WhatsAppMessage {
  id: string;
  from: string;
  message: string;
  timestamp: Date;
  type: 'incoming' | 'outgoing';
}

export interface AutoReplyConfig {
  enabled: boolean;
  message: string;
  delay: number; // in milliseconds
  conditions?: {
    keywords?: string[];
    timeOfDay?: {
      start: string; // HH:MM format
      end: string; // HH:MM format
    };
  };
}

class WhatsAppService {
  private phoneNumber: string;
  private autoReplyConfig: AutoReplyConfig;
  private messageHistory: WhatsAppMessage[] = [];

  constructor(phoneNumber: string, autoReplyConfig?: Partial<AutoReplyConfig>) {
    this.phoneNumber = phoneNumber;
    this.autoReplyConfig = {
      enabled: true,
      message: "Thank you for reaching out! 🌍 We serve clients worldwide and I'll get back to you within 24 hours with a personalized quote for your project. In the meantime, feel free to explore our portfolio and services on our website.",
      delay: 2000,
      ...autoReplyConfig
    };
  }

  // Initialize WhatsApp chat
  public initChat(autoMessage?: string): void {
    const message = autoMessage || "Hi! I'm interested in your web development services. Can you help me get started?";
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${this.phoneNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappUrl, '_blank');
    
    // Log the interaction
    this.logMessage({
      id: Date.now().toString(),
      from: 'user',
      message: message,
      timestamp: new Date(),
      type: 'outgoing'
    });

    // Simulate auto-reply if enabled
    if (this.autoReplyConfig.enabled) {
      this.scheduleAutoReply();
    }
  }

  // Schedule auto-reply
  private scheduleAutoReply(): void {
    setTimeout(() => {
      this.sendAutoReply();
    }, this.autoReplyConfig.delay);
  }

  // Send auto-reply
  private sendAutoReply(): void {
    const replyMessage = this.generateAutoReply();
    
    // Log the auto-reply
    this.logMessage({
      id: Date.now().toString(),
      from: 'business',
      message: replyMessage,
      timestamp: new Date(),
      type: 'outgoing'
    });

    // In a real implementation, this would use WhatsApp Business API
    console.log('Auto-reply sent:', replyMessage);
    
    // You can integrate with actual WhatsApp Business API here
    // this.sendViaWhatsAppAPI(replyMessage);
  }

  // Generate contextual auto-reply based on conditions
  private generateAutoReply(): string {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;

    // Check if within business hours
    if (this.autoReplyConfig.conditions?.timeOfDay) {
      const { start, end } = this.autoReplyConfig.conditions.timeOfDay;
      if (currentTime < start || currentTime > end) {
        return "Thank you for your message! 🌍 We serve clients worldwide and I'll get back to you within 24 hours with a personalized quote for your project. In the meantime, feel free to explore our portfolio and services on our website.";
      }
    }

    return this.autoReplyConfig.message;
  }

  // Log message to history
  private logMessage(message: WhatsAppMessage): void {
    this.messageHistory.push(message);
    
    // Keep only last 100 messages
    if (this.messageHistory.length > 100) {
      this.messageHistory = this.messageHistory.slice(-100);
    }
  }

  // Get message history
  public getMessageHistory(): WhatsAppMessage[] {
    return [...this.messageHistory];
  }

  // Update auto-reply configuration
  public updateAutoReplyConfig(config: Partial<AutoReplyConfig>): void {
    this.autoReplyConfig = { ...this.autoReplyConfig, ...config };
  }

  // Get current auto-reply configuration
  public getAutoReplyConfig(): AutoReplyConfig {
    return { ...this.autoReplyConfig };
  }

  // Check if business hours
  public isBusinessHours(): boolean {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;

    if (this.autoReplyConfig.conditions?.timeOfDay) {
      const { start, end } = this.autoReplyConfig.conditions.timeOfDay;
      return currentTime >= start && currentTime <= end;
    }

    return true; // Default to always available
  }

  // Get phone number
  public getPhoneNumber(): string {
    return this.phoneNumber;
  }

  // Format phone number for display
  public getFormattedPhoneNumber(): string {
    const cleaned = this.phoneNumber.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{2})(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `+${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
    }
    return this.phoneNumber;
  }
}

// Create and export default instance
const whatsappService = new WhatsAppService('923044887447', {
  conditions: {
    timeOfDay: {
      start: '09:00',
      end: '18:00'
    }
  }
});

export default whatsappService;
